import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, MessageCircle, Newspaper, UserRound } from "lucide-react";

import { GenericObjectInterface } from "@/src/utilities";
import Link from "next/link";
import {
  formatDate,
  formatTitle,
  generateUrlSlug,
  normalizeImageSrc,
} from "@/src/utilities/helper/helper";
import Image from "next/image";
import { constantText } from "@/src/utilities/constants/constants";
import { images } from "@/public";
import _ from "lodash";

interface ArticleCardProps {
  post: GenericObjectInterface;
  index: number;
  variant?:
  | "featured"
  | "newsletters"
  | "whitepapers"
  | "case-studies"
  | "webinar-videos"
  | "infographics"
  | "tech-news"
  | "interview-podcasts";
  type?:
  | "blog"
  | "newsletters"
  | "tech-news"
  | "whitepapers"
  | "case-studies"
  | "webinar-videos"
  | "infographics"
  | "interview-podcasts";
}

/**
 *
 * @param {ArticleCardProps} props
 * @description This component is used to display a single card
 * as per the variant
 * featured -> blog
 * newsletters -> newsletters
 * whitepapers -> whitepapers
 * case-studies -> case-studies
 * @returns {React.FC}
 */
export const ArticleCard: React.FC<ArticleCardProps> = ({
  post,
  index,
  variant,
  type = "blog",
}) => {
  console.log("ArticleCard post:", post, post.updatedAt);

  const getHref = () => {
    if (variant === "webinar-videos") {
      return `/webinar-videos/${post?.documentId}`;
    } else if (variant === "interview-podcasts") {
      return `/interview-podcasts/${post?.documentId}`;
    }
    if (variant === "tech-news") return post?.url || "#";
    return `${type}/${generateUrlSlug(post?.title, post?.documentId)}`;
  };

  return (
    <Link
      href={getHref()}
      key={index}
      {...(variant === "tech-news"
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
    >
      {variant === "featured" ? (
        <Card
          key={post.id}
          className="overflow-hidden hover:shadow-lg transition-shadow h-full py-0 rounded-sm border border-gray-300 gap-0"
        >
          <div className="relative h-48 bg-gray-100">
            {post?.thumbnail_url ? (
              <Image
                width={600}
                height={400}
                src={post.thumbnail_url}
                alt={post.title}
                className="w-full h-full object-cover bg-gray-200"
                onError={(e) => {
                  e.currentTarget.src = images.dummyProduct;
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Newspaper className="h-12 w-12 text-gray-400" />
              </div>
            )}
            {/* {post?.featured && (
              <Badge className="absolute top-3 left-3 bg-primary text-white">
                Editor's Pick
              </Badge>
            )} */}
          </div>
          <div className="p-4 flex flex-1 flex-col justify-between gap-4">
            <div className="flex flex-col gap-2">
              <h3 className="font-semibold text-gray-900 line-clamp-2 hover:text-primary transition-colors text-lg">
                {formatTitle(post?.title)}
              </h3>
              {/* <p className="text-sm text-gray-600 line-clamp-3 mb-3">
              {post?.description}
            </p> */}
              {post?.tags && (
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground rounded-xs">
                    {post?.tags?.split(" ")[0]}
                  </Badge>
                  <Badge className="bg-muted text-muted-foreground rounded-xs">
                    {post?.tags?.split(" ")[1]}
                  </Badge>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="justify-center items-center flex gap-2">
                <span className="p-1 bg-primary/90 rounded-full">
                  <UserRound className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs text-black font-medium">
                  {post?.author?.name || constantText.BY_AI}
                </span>
              </div>
              <div className="justify-center items-center flex">
                <span className="text-xs text-gray-500 font-medium">
                  {formatDate(post?.updatedAt, "MMM DD, YYYY")}
                </span>
              </div>
            </div>

            {/* <div className="text-xs text-gray-500 flex items-center gap-1">
              <span className="font-medium text-primary">
                {post?.author?.name || constantText.BY_AI}
              </span>
              <span>•</span>
              <span>{formatDate(post?.publishedAt)}</span>
              <span>•</span>
              <span>{post?.read_time || "4 min"} read</span>
            </div> */}
          </div>
        </Card>
      ) : variant === "newsletters" ? (
        <Card
          key={post.id}
          className="overflow-hidden hover:shadow-lg transition-shadow h-full py-0 rounded-sm border border-gray-300 gap-0"
        >
          <div className="relative h-48 bg-gray-100">
            {post?.images ? (
              <Image
                width={600}
                height={400}
                src={normalizeImageSrc(post.images)[0]}
                alt={post.title}
                className="w-full h-full object-cover bg-gray-100"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <MessageCircle className="h-12 w-12 text-gray-400" />
              </div>
            )}
            {post?.featured && (
              <Badge className="absolute top-3 left-3 bg-primary text-white">
                Editor's Pick
              </Badge>
            )}
          </div>
          <div className="p-4 flex flex-1 flex-col justify-between">
            <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 hover:text-primary transition-colors">
              {formatTitle(post?.title)}
            </h3>
            {/* <p className="text-sm text-gray-600 line-clamp-3 mb-3">
              {post?.description}
            </p> */}

            <div className="flex items-center justify-between gap-2">
              <div className="justify-center items-center flex gap-2">
                <span className="p-1 bg-primary/90 rounded-full">
                  <UserRound className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs text-black font-medium">
                  {post?.author?.name || constantText.BY_AI}
                </span>
              </div>
              <div className="justify-center items-center flex">
                <span className="text-xs text-gray-500 font-medium">
                  {formatDate(post?.updatedAt, "MMM DD, YYYY")}
                </span>
              </div>
            </div>

            {/* <div className="text-xs text-gray-500 flex items-center gap-1">
              <span className="font-medium text-primary">
                {post?.author?.name || constantText.BY_AI}
              </span>
              <span>•</span>
              <span>{formatDate(post?.updatedAt)}</span>
              <span>•</span>
              <span>{post?.read_time || "4 min"} read</span>
            </div> */}
          </div>
        </Card>
      ) : variant === "whitepapers" ? (
        <Card
          key={post.id}
          className="overflow-hidden hover:shadow-lg transition-shadow h-full py-0 rounded-sm border border-gray-300"
        >
          <div className="relative h-96 bg-gray-100">
            {post?.thumbnail?.url ? (
              <img
                src={post.thumbnail?.url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Newspaper className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-4 flex flex-1 flex-col justify-between">
            <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 hover:text-primary transition-colors">
              {formatTitle(post?.title)}
            </h3>
            {/* <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {post?.description}
            </p> */}

            <div className="flex items-center justify-between gap-2">
              <div className="justify-center items-center flex gap-2">
                <span className="p-1 bg-primary/90 rounded-full">
                  <UserRound className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs text-black font-medium">
                  {post?.author?.name || constantText.BY_AI}
                </span>
              </div>
              <div className="justify-center items-center flex">
                <span className="text-xs text-gray-500 font-medium">
                  {formatDate(post?.updatedAt, "MMM DD, YYYY")}
                </span>
              </div>
            </div>

            {/* <div className="text-xs text-gray-500 flex items-center gap-1">
              <span>{formatDate(post?.updatedAt)}</span>
              <span>•</span>
              <span>{post?.read_time || "4 min"} read</span>
            </div> */}
          </div>
        </Card>
      ) : variant === "case-studies" ? (
        <Card
          key={post.id}
          className="overflow-hidden hover:shadow-lg transition-shadow h-full py-0 border border-gray-300 rounded-sm"
        >
          <div className="relative h-96 bg-gray-100">
            {post?.thumbnail?.url ? (
              <img
                src={post.thumbnail?.url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Newspaper className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-4 flex flex-1 flex-col justify-between">
            <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 hover:text-primary transition-colors">
              {formatTitle(post?.title)}
            </h3>
            {/* <p className="text-sm text-gray-600 line-clamp-2 mb-3">
              {post?.description}
            </p> */}

            <div className="flex items-center justify-between gap-2">
              <div className="justify-center items-center flex gap-2">
                <span className="p-1 bg-primary/90 rounded-full">
                  <UserRound className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs text-black font-medium">
                  {post?.author?.name || constantText.BY_AI}
                </span>
              </div>
              <div className="justify-center items-center flex">
                <span className="text-xs text-gray-500 font-medium">
                  {formatDate(post?.updatedAt, "MMM DD, YYYY")}
                </span>
              </div>
            </div>
            {/* <div className="text-xs text-gray-500 flex items-center gap-1">
              <span>{formatDate(post?.updatedAt)}</span>
              <span>•</span>
              <span>{post?.read_time || "4 min"} read</span>
            </div> */}
          </div>
        </Card>
      ) : variant === "webinar-videos" ? (
        <Card
          key={post.id}
          className="overflow-hidden hover:shadow-lg transition-shadow h-full py-0 border border-gray-300 rounded-sm cursor-pointer flex flex-col"
        >
          <div className="relative h-48 bg-gray-100 group">
            {post?.thumbnail?.url ? (
              <>
                <Image
                  width={600}
                  height={400}
                  src={post.thumbnail?.url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                {/* YouTube Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-14 bg-red-600 rounded-lg flex items-center justify-center group-hover:bg-red-700 transition-colors">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-10 h-10 fill-white ml-1"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <Newspaper className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-4 flex flex-1 flex-col gap-3 bg-white">
            <h3 className="font-semibold text-gray-900 line-clamp-2 hover:text-primary transition-colors text-base">
              {formatTitle(post?.title)}
            </h3>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100 mt-auto">
              <div className="flex items-center gap-2">
                <span className="p-1 bg-primary/90 rounded-full">
                  <UserRound className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs text-black font-medium">
                  {post?.author || constantText.BY_AI}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 font-medium">
                  {formatDate(
                    post?.updatedAt || post?.publishedAt,
                    "MMM DD, YYYY"
                  )}
                </span>
              </div>
            </div>
          </div>
        </Card>
      ) : variant === "interview-podcasts" ? (
        <Card
          key={post.id}
          className="overflow-hidden hover:shadow-lg transition-shadow h-full py-0 border border-gray-300 rounded-sm cursor-pointer flex flex-col"
        >
          <div className="relative h-48 bg-gray-100 group">
            {post?.thumbnail?.url ? (
              <>
                <img
                  src={post.thumbnail?.url}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-14 bg-red-600 rounded-lg flex items-center justify-center group-hover:bg-red-700 transition-colors">
                    <svg
                      viewBox="0 0 24 24"
                      className="w-10 h-10 fill-white ml-1"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full">
                <Newspaper className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-4 flex flex-1 flex-col gap-3 bg-white">
            <h3 className="font-semibold text-gray-900 line-clamp-2 hover:text-primary transition-colors text-base">
              {formatTitle(post?.title)}
            </h3>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100 mt-auto">
              <div className="flex items-center gap-2">
                <span className="p-1 bg-primary/90 rounded-full">
                  <UserRound className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs text-black font-medium">
                  {post?.author || constantText.BY_AI}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 font-medium">
                  {formatDate(
                    post?.updatedAt || post?.publishedAt,
                    "MMM DD, YYYY"
                  )}
                </span>
              </div>
            </div>
          </div>
        </Card>
      ) : variant === "infographics" ? (
        <Card
          key={post.id}
          className="overflow-hidden hover:shadow-lg transition-shadow h-full py-0 border border-gray-300 rounded-lg cursor-pointer"
        >
          <div className="relative h-64 bg-gray-100">
            {post?.image?.url ? (
              <img
                src={post.image.url}
                alt={post.title || "Infographic"}
                className="w-full h-full object-cover object-top"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <Newspaper className="h-12 w-12 text-gray-400" />
              </div>
            )}
          </div>
          <div className="p-4 flex flex-col gap-3 bg-white">
            <h3 className="font-semibold text-gray-900 line-clamp-2 hover:text-primary transition-colors text-base">
              {_.isEmpty(post?.title)
                ? "Untitled Infographic"
                : formatTitle(post?.title)}
            </h3>

            {post?.description && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {post.description}
              </p>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <span className="p-1 bg-primary/90 rounded-full">
                  <UserRound className="h-4 w-4 text-white" />
                </span>
                <span className="text-xs text-black font-medium">
                  {post?.author || constantText.BY_AI}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500 font-medium">
                  {formatDate(
                    post?.updatedAt || post?.publishedAt,
                    "MMM DD, YYYY"
                  )}
                </span>
              </div>
            </div>
          </div>
        </Card>
      ) : variant === "tech-news" ? (
        <Card
          key={post.id}
          className="p-0 relative flex flex-col h-full hover:shadow-lg transition-shadow"
        >
          {/* Thumbnail */}
          {post?.thumbnail_url ? (
            <div className="w-full h-40 overflow-hidden">
              <Image
                width={600}
                height={400}
                src={post.thumbnail_url}
                alt={post.title}
                className="object-cover p-1 rounded-t-xl w-full h-full group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.currentTarget.src = images.dummyProduct;
                }}
              />
            </div>
          ) : (
            <div className="w-full h-40 flex items-center justify-center bg-gray-100 text-muted-foreground text-sm">
              No preview available
            </div>
          )}

          {/* Badge */}
          {post?.featured && (
            <CardHeader className="absolute top-2 left-2 p-0">
              <Badge variant="default" className="text-xs">
                {"Editor's Pick"}
              </Badge>
            </CardHeader>
          )}

          {/* Title */}
          <div className="px-3 pt-2">
            <h3 className="text-md font-semibold line-clamp-2 hover:text-primary transition-colors">
              {formatTitle(post?.title)}
            </h3>
          </div>

          {/* Description + Meta */}
          <CardContent className="mt-2 flex flex-col flex-grow px-3 pb-3 justify-end">
            <p className="text-sm text-muted-foreground mb-2 line-clamp-3">
              {post?.description}
            </p>

            {/* Push meta to bottom */}
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <span className="font-medium text-orange-600">
                {post?.author?.name || constantText.BY_AI}
              </span>
              <span>•</span>
              <span>{formatDate(post?.createdAt)}</span>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </Link>
  );
};
